import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { FormType, GetScheduleFormFormsRequestDTO, GetScheduleFormFormsResponseDTO, MODULE, RecordState } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { format } from 'date-fns';

import { ArchiveConfirmationDialog } from '@/components/common/archive-confirmation-dialog';
import { DeleteConfirmationDialog } from '@/components/common/delete-confirmation-dialog';
import { RestoreConfirmationDialog } from '@/components/common/restore-confirmation-dialog';
import { ShareDialog } from '@/components/common/share-dialog';
import ArchiveIcon from '@/components/icons/archive';
import CloneIcon from '@/components/icons/clone';
import DeleteIcon from '@/components/icons/delete';
import FolderOpenIcon from '@/components/icons/folder-open';
import FormTemplateIcon from '@/components/icons/form-template';
import GridIcon from '@/components/icons/grid';
import LinkViewIcon from '@/components/icons/link-view';
import PencilIcon from '@/components/icons/pencil';
import PlusIcon from '@/components/icons/plus';
import RegisterIcon from '@/components/icons/register';
import UndoIcon from '@/components/icons/undo';
import { TablePageGridView } from '@/components/table-page/table-page-grid-view';
import { TablePageHeader, TablePageHeaderActionButton } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { TablePageTitle } from '@/components/table-page/table-page-title';
import { useTopbarStore } from '@/components/topbar/topbar-store';
import { ButtonStateToggle } from '@/components/ui/button-state-toggle';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { env } from '@/env';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import ShareIcon from '@/legacy/components/icons/share';
import { getScheduleFormFormsInfiniteQueryOptions, scheduleFormKeys } from '@/queries/schedule';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { useUserStore } from '@/stores/user.store';
import { useEditFormNavigate, useWorkspaceNavigate } from '@/utils/routes';

import { TopbarActions } from '../topbar-actions';
import {
  NoScheduleFormFormsCreatedDisplay,
  NoScheduleFormFormsFoundFromFiltersDisplay,
  NoScheduleFormFormsFoundFromSearchDisplay,
  ScheduleFormFormsTable,
} from './table';
import { createScheduleFormFormsTableColumns, FILTER_FIELD_MAP } from './table-meta';

function ScheduleFormFormsPage() {
  const queryClient = useQueryClient();
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const toast = useToast();
  const navigate = useWorkspaceNavigate();
  const handleEditScheduleFormForm = useEditFormNavigate();
  const { setModuleFilters, syncModuleColumns } = useModulesStore();
  const { canManageScheduleForms } = usePermissions();

  const { org } = useUserStore();

  const {
    config: { columnOrder, grouping, sorting, filters, view, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.ScheduleFormForms, {
    filterFieldMap: FILTER_FIELD_MAP,
    columns: createScheduleFormFormsTableColumns({ canManageScheduleForms }),
  });

  const [openArchiveConfirmation, setOpenArchiveConfirmation] = useState(false);
  const [openRestoreConfirmation, setOpenRestoreConfirmation] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [scheduleFormFormState, setScheduleFormFormState] = useState<RecordState>(RecordState.Active);
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  useLayoutEffect(() => {
    useTopbarStore.getState().setActions(<TopbarActions />);

    return () => {
      useTopbarStore.getState().setActions(<></>);
    };
  }, []);

  const {
    data: scheduleFormFormsData,
    fetchNextPage: fetchNextScheduleFormFormsPage,
    hasNextPage: hasNextScheduleFormFormsPage,
    isFetching: isFetchingScheduleFormForms,
    isFetchingNextPage: isFetchingNextScheduleFormFormsPage,
    error: scheduleFormFormsError,
  } = useInfiniteQuery(
    getScheduleFormFormsInfiniteQueryOptions({
      filters: {
        state: scheduleFormFormState,
        search: debouncedSearch,
        fields: Object.fromEntries(
          Object.entries(FILTER_FIELD_MAP).map(([id, key]) => [
            key,
            id === 'number'
              ? ((filters || []).find((filter) => filter.id === id)?.value as string)?.replace(/[^0-9]/g, '')
              : (filters || []).find((filter) => filter.id === id)?.value,
          ]),
        ),
      },
      orderBy: sorting.key
        ? { field: sorting.key as NonNullable<GetScheduleFormFormsRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      enabled: !isLoadingPageConfig,
    }),
  );

  const getSelectedScheduleFormForms = useMemo(() => {
    return (incomingData: typeof scheduleFormFormsData) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const scheduleFormForms = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => scheduleFormForms[parseInt(id)]);
    };
  }, [selectedRows]);

  const { mutateAsync: bulkUpdateFormState, isPending: isUpdatingFormState } = useMutation({
    mutationFn: async ({ ids, state }: { ids: string[]; state: RecordState }) => await ApiService.BulkUpdateFormState({ ids, state }),
    onSuccess: (_, { state }) => {
      const messages = {
        [RecordState.Active]: 'Schedule forms restored successfully',
        [RecordState.Archived]: 'Schedule forms archived successfully',
        [RecordState.Deleted]: 'Schedule forms deleted successfully',
      };
      toast.success(messages[state]);
      setOpenArchiveConfirmation(false);
      setOpenRestoreConfirmation(false);
      setOpenDeleteConfirmation(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: scheduleFormKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  function handleArchiveScheduleFormForms() {
    if (scheduleFormFormsData?.pages) {
      const toArchive = getSelectedScheduleFormForms(scheduleFormFormsData);
      bulkUpdateFormState({ ids: toArchive.map((scheduleFormForm) => scheduleFormForm.id), state: RecordState.Archived });
    }
  }

  function handleRestoreScheduleFormForms() {
    if (scheduleFormFormsData?.pages) {
      const toRestore = getSelectedScheduleFormForms(scheduleFormFormsData);
      bulkUpdateFormState({ ids: toRestore.map((scheduleFormForm) => scheduleFormForm.id), state: RecordState.Active });
    }
  }

  function handleDeleteScheduleFormForms() {
    if (scheduleFormFormsData?.pages) {
      const toDelete = getSelectedScheduleFormForms(scheduleFormFormsData);
      bulkUpdateFormState({ ids: toDelete.map((scheduleFormForm) => scheduleFormForm.id), state: RecordState.Deleted });
    }
  }

  const { mutate: cloneScheduleFormForms, isPending: isCloningScheduleFormForms } = useMutation({
    mutationFn: async (formIds: string[]) => await ApiService.CloneForms({ formIds }),
    onSuccess: () => {
      toast.success('Schedule forms cloned successfully');
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: scheduleFormKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  function handleCloneScheduleFormForms() {
    cloneScheduleFormForms(getSelectedScheduleFormForms(scheduleFormFormsData).map((scheduleFormForm) => scheduleFormForm.id));
  }

  function handleAddScheduleFormForm() {
    navigate(`/forms/create?formType=${FormType.ScheduleForm}`);
  }

  function handleGridItemClick(item: GetScheduleFormFormsResponseDTO['data'][number]) {
    navigate(`/schedules/forms/${item.id}/submissions`);
  }

  const selectedScheduleFormForms = useMemo(() => getSelectedScheduleFormForms(scheduleFormFormsData), [scheduleFormFormsData, getSelectedScheduleFormForms]);

  useEffect(() => {
    if (scheduleFormFormsError) {
      toast.error(scheduleFormFormsError.message);
    }
  }, [toast, scheduleFormFormsError]);

  useResetRowSelectionOnChange(setSelectedRows, [scheduleFormFormState, debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextScheduleFormFormsPage,
    hasNextPage: hasNextScheduleFormFormsPage,
    isFetching: isFetchingScheduleFormForms,
    isError: !!scheduleFormFormsError,
    root: scrollRoot,
  });

  const flattenedData = useMemo(() => scheduleFormFormsData?.pages.flatMap((page) => page.data) ?? [], [scheduleFormFormsData]);

  useEffect(() => {
    if (!view) {
      updatePageConfig({
        view: 'grid',
      });
    }
  }, [view, updatePageConfig]);

  useEffect(() => {
    syncModuleColumns(MODULE.ScheduleFormForms, createScheduleFormFormsTableColumns());
  }, [syncModuleColumns]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            {!selectedScheduleFormForms.length && (
              <>
                <div className="flex h-full items-center gap-x-2 px-2">
                  <TablePageTitle>Forms</TablePageTitle>
                  <ButtonStateToggle
                    options={[
                      { icon: <FolderOpenIcon />, label: 'Active', value: RecordState.Active },
                      { icon: <ArchiveIcon />, label: 'Archived', value: RecordState.Archived },
                    ]}
                    value={scheduleFormFormState}
                    onChange={setScheduleFormFormState}
                    className="font-medium"
                    responsive
                  />
                  <ButtonStateToggle
                    options={[
                      { icon: <GridIcon />, label: 'Grid', value: 'grid' },
                      { icon: <RegisterIcon />, label: 'Register', value: 'register' },
                    ]}
                    value={view}
                    onChange={(view) =>
                      updatePageConfig({
                        view: view as 'grid' | 'register',
                      })
                    }
                    className="font-medium"
                    responsive
                  />
                  <TablePageSearchBar search={search} setSearch={setSearch} />
                  {filters && filters.length > 0 && (
                    <TablePageHeaderActionButton
                      icon={<UndoIcon />}
                      label="Remove all Filters"
                      kind="secondary"
                      onClick={() => setModuleFilters(MODULE.ScheduleFormForms, [])}
                    />
                  )}
                </div>

                <div className="mr-1 flex h-full items-center">
                  <div className="flex h-full items-center">
                    <TablePageHeaderActionButton
                      onClick={() => setShareUrl(`${env.VITE_FORMS_URL}/${org?.id}?rawRedirect=true`)}
                      icon={<ShareIcon />}
                      label="Share Link"
                    />
                    <TablePageHeaderActionButton
                      onClick={() => window.open(`${env.VITE_FORMS_URL}/${org?.id}?rawRedirect=true`, '_blank')}
                      icon={<LinkViewIcon />}
                      label="Open Mechanic App"
                    />
                  </div>
                  {canManageScheduleForms && (
                    <TablePageHeaderActionButton
                      variant="success"
                      icon={<PlusIcon />}
                      kind="primary"
                      onClick={handleAddScheduleFormForm}
                      label="Create Schedule Form"
                    />
                  )}
                </div>
              </>
            )}

            {selectedScheduleFormForms.length > 0 && canManageScheduleForms && (
              <div className="flex items-center gap-1 px-2">
                <TablePageSelectedCountIndicator
                  selectedCount={selectedScheduleFormForms.length}
                  totalCount={scheduleFormFormsData?.pages[0]?.totalCount ?? 0}
                />
                {selectedScheduleFormForms.length === 1 && (
                  <TablePageHeaderActionButton
                    icon={<PencilIcon />}
                    onClick={() => handleEditScheduleFormForm(selectedScheduleFormForms[0].id)}
                    label="Edit Form"
                  />
                )}

                {selectedScheduleFormForms.length > 0 && (
                  <TablePageHeaderActionButton
                    icon={<CloneIcon />}
                    disabled={isCloningScheduleFormForms}
                    onClick={handleCloneScheduleFormForms}
                    label={selectedScheduleFormForms.length > 1 ? 'Clone Forms' : 'Clone Form'}
                  />
                )}

                {scheduleFormFormState === RecordState.Active && (
                  <TablePageHeaderActionButton icon={<ArchiveIcon />} onClick={() => setOpenArchiveConfirmation(true)} label="Archive Selected" />
                )}
                {scheduleFormFormState === RecordState.Archived && (
                  <>
                    <TablePageHeaderActionButton icon={<UndoIcon />} onClick={() => setOpenRestoreConfirmation(true)} label="Restore Selected" />
                    <TablePageHeaderActionButton
                      icon={<DeleteIcon className="text-default-red" />}
                      onClick={() => setOpenDeleteConfirmation(true)}
                      label="Delete Selected"
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </TablePageHeader>

      {view === 'register' && (
        <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
          <ScrollBar orientation="horizontal" />
          <ScheduleFormFormsTable
            data={flattenedData}
            loading={(isFetchingScheduleFormForms && !isFetchingNextScheduleFormFormsPage) || isLoadingPageConfig}
            notFoundDisplay={
              debouncedSearch.length > 0 ? (
                <NoScheduleFormFormsFoundFromSearchDisplay />
              ) : filters && filters.length > 0 ? (
                <NoScheduleFormFormsFoundFromFiltersDisplay
                  addForm={handleAddScheduleFormForm}
                  removeColumnFilters={() => setModuleFilters(MODULE.ScheduleFormForms, [])}
                />
              ) : (
                <NoScheduleFormFormsCreatedDisplay addForm={handleAddScheduleFormForm} />
              )
            }
            rowSelection={selectedRows}
            onRowSelectionChange={setSelectedRows}
            columnFilters={filters || []}
            onColumnFiltersChange={(filters) => setModuleFilters(MODULE.ScheduleFormForms, filters)}
            sorting={sorting}
            grouping={grouping}
            columnOrder={columnOrder}
            columnVisibility={columnVisibility}
            onSortingChange={(newSorting) =>
              updatePageConfig({
                table: { sorting: newSorting },
              })
            }
          />
          {hasNextScheduleFormFormsPage && (
            <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
              {isFetchingNextScheduleFormFormsPage ? 'Loading more...' : 'Load Newer'}
            </Button>
          )}
        </ScrollArea>
      )}

      {view === 'grid' && (
        <div ref={setScrollRoot} className="scrollbar scrollbar-track-transparent scrollbar-thumb-grey-5 overflow-y-auto">
          <TablePageGridView<GetScheduleFormFormsResponseDTO['data'][number]>
            data={flattenedData}
            notFoundDisplay={<NoScheduleFormFormsCreatedDisplay addForm={handleAddScheduleFormForm} />}
            loading={(isFetchingScheduleFormForms && !isFetchingNextScheduleFormFormsPage) || isLoadingPageConfig}
            ItemComponent={({ item, handleSelectedItemsChange, index }) => (
              <div className="flex flex-col gap-2 p-2">
                <div className="flex w-full justify-between">
                  <div className="flex items-center gap-[2px]">
                    <FormTemplateIcon />
                  </div>
                  <label className="-m-2 cursor-pointer p-2" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selectedRows[index]} onCheckedChange={() => handleSelectedItemsChange(index)} />
                  </label>
                </div>

                <div className="flex flex-col gap-2">
                  <div>
                    <div className="line-clamp-2 min-h-[2lh] text-sm font-[700] break-words">{item.name}</div>
                    <div className="text-secondary-text text-xs">FR{item.number}</div>
                  </div>

                  <div className="text-secondary-text text-xs">
                    <div>Forms: {item.submissionCount}</div>
                    <div>Version: {item.revision}</div>
                    <div>Updated: {format(new Date(item.updatedAt), 'MMM dd, yyyy')}</div>
                  </div>
                </div>
              </div>
            )}
            selectedItems={selectedRows}
            onSelectedItemsChange={setSelectedRows}
            onItemClick={handleGridItemClick}
          />
          {hasNextScheduleFormFormsPage && (
            <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
              {isFetchingNextScheduleFormFormsPage ? 'Loading more...' : 'Load Newer'}
            </Button>
          )}
        </div>
      )}

      <ArchiveConfirmationDialog
        open={openArchiveConfirmation}
        onOpenChange={setOpenArchiveConfirmation}
        callback={handleArchiveScheduleFormForms}
        loading={isUpdatingFormState}
        title="Archive schedule forms?"
        description={
          <>
            Are you sure you want to archive these schedule forms? You can restore them later from the archived tab.
            {selectedScheduleFormForms.some((form) => form.deploymentRuleCount > 0) && (
              <>
                <br />
                <br />
                <b>Note:</b> Archiving forms that are in deployment rules will remove them from those rules. You can restore forms later from the Archived tab.
              </>
            )}
          </>
        }
      />
      <RestoreConfirmationDialog
        open={openRestoreConfirmation}
        onOpenChange={setOpenRestoreConfirmation}
        callback={handleRestoreScheduleFormForms}
        loading={isUpdatingFormState}
        title="Restore schedule forms?"
        description="Are you sure you want to restore these schedule forms? They will be moved back to your active schedule forms."
      />
      <DeleteConfirmationDialog
        open={openDeleteConfirmation}
        onOpenChange={setOpenDeleteConfirmation}
        callback={handleDeleteScheduleFormForms}
        loading={isUpdatingFormState}
        title="Delete schedule forms?"
        description="This action is permanent and cannot be undone. Are you sure you want to delete these schedule forms?"
      />

      {shareUrl && <ShareDialog url={shareUrl} onOpenChange={() => setShareUrl(null)} open={!!shareUrl} />}
    </div>
  );
}

export default ScheduleFormFormsPage;
